import React, { useState } from 'react';

/**
 * AnalyticsCharts - Advanced financial analysis charts
 * Professional charts for deep financial insights and trend analysis
 */

// Financial Health Score - Multi-metric circular dashboard
export const FinancialHealthScore = ({ metrics, height = 200 }) => {
  const { 
    savingsRate = 0, 
    debtToIncome = 0, 
    expenseRatio = 0, 
    emergencyFund = 0 
  } = metrics || {};

  const center = 100;
  const radius = 70;
  
  // Calculate overall health score (0-100)
  const healthScore = Math.max(0, Math.min(100, 
    (savingsRate * 0.3) + 
    ((100 - debtToIncome) * 0.25) + 
    ((100 - expenseRatio) * 0.25) + 
    (emergencyFund * 0.2)
  ));

  const getScoreColor = (score) => {
    if (score >= 80) return 'var(--income-green)';
    if (score >= 60) return 'var(--warning-orange)';
    return 'var(--expense-red)';
  };

  const scoreColor = getScoreColor(healthScore);
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (healthScore / 100) * circumference;

  return (
    <div style={{ 
      width: '100%', 
      height: height, 
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '2rem'
    }}>
      {/* Main health score circle */}
      <div style={{ flex: '0 0 200px', textAlign: 'center' }}>
        <svg width={200} height={200} viewBox="0 0 200 200">
          {/* Background circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="var(--gray-200)"
            strokeWidth="12"
          />
          
          {/* Health score arc */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={scoreColor}
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            transform={`rotate(-90 ${center} ${center})`}
            style={{ transition: 'stroke-dashoffset 0.6s ease-out' }}
          />
          
          {/* Center score */}
          <text
            x={center}
            y={center - 8}
            textAnchor="middle"
            fontSize="32"
            fontWeight="700"
            fill={scoreColor}
          >
            {Math.round(healthScore)}
          </text>
          <text
            x={center}
            y={center + 20}
            textAnchor="middle"
            fontSize="14"
            fill="var(--gray-500)"
            fontWeight="500"
          >
            Health Score
          </text>
        </svg>
      </div>

      {/* Metrics breakdown */}
      <div style={{ flex: 1 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          {/* Savings Rate */}
          <div style={{ padding: '0.75rem', background: 'var(--gray-50)', borderRadius: '8px' }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.25rem' }}>
              Savings Rate
            </div>
            <div style={{ fontSize: '1.125rem', fontWeight: '600', color: getScoreColor(savingsRate) }}>
              {Math.round(savingsRate)}%
            </div>
            <div style={{ width: '100%', height: '4px', background: 'var(--gray-200)', borderRadius: '2px', marginTop: '0.5rem' }}>
              <div 
                style={{ 
                  width: `${Math.min(100, savingsRate)}%`, 
                  height: '100%', 
                  background: getScoreColor(savingsRate),
                  borderRadius: '2px',
                  transition: 'width 0.3s ease'
                }} 
              />
            </div>
          </div>

          {/* Expense Ratio */}
          <div style={{ padding: '0.75rem', background: 'var(--gray-50)', borderRadius: '8px' }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.25rem' }}>
              Expense Ratio
            </div>
            <div style={{ fontSize: '1.125rem', fontWeight: '600', color: getScoreColor(100 - expenseRatio) }}>
              {Math.round(expenseRatio)}%
            </div>
            <div style={{ width: '100%', height: '4px', background: 'var(--gray-200)', borderRadius: '2px', marginTop: '0.5rem' }}>
              <div 
                style={{ 
                  width: `${Math.min(100, expenseRatio)}%`, 
                  height: '100%', 
                  background: getScoreColor(100 - expenseRatio),
                  borderRadius: '2px',
                  transition: 'width 0.3s ease'
                }} 
              />
            </div>
          </div>

          {/* Debt to Income */}
          <div style={{ padding: '0.75rem', background: 'var(--gray-50)', borderRadius: '8px' }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.25rem' }}>
              Debt to Income
            </div>
            <div style={{ fontSize: '1.125rem', fontWeight: '600', color: getScoreColor(100 - debtToIncome) }}>
              {Math.round(debtToIncome)}%
            </div>
            <div style={{ width: '100%', height: '4px', background: 'var(--gray-200)', borderRadius: '2px', marginTop: '0.5rem' }}>
              <div 
                style={{ 
                  width: `${Math.min(100, debtToIncome)}%`, 
                  height: '100%', 
                  background: getScoreColor(100 - debtToIncome),
                  borderRadius: '2px',
                  transition: 'width 0.3s ease'
                }} 
              />
            </div>
          </div>

          {/* Emergency Fund */}
          <div style={{ padding: '0.75rem', background: 'var(--gray-50)', borderRadius: '8px' }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.25rem' }}>
              Emergency Fund
            </div>
            <div style={{ fontSize: '1.125rem', fontWeight: '600', color: getScoreColor(emergencyFund) }}>
              {Math.round(emergencyFund)}%
            </div>
            <div style={{ width: '100%', height: '4px', background: 'var(--gray-200)', borderRadius: '2px', marginTop: '0.5rem' }}>
              <div 
                style={{ 
                  width: `${Math.min(100, emergencyFund)}%`, 
                  height: '100%', 
                  background: getScoreColor(emergencyFund),
                  borderRadius: '2px',
                  transition: 'width 0.3s ease'
                }} 
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Cash Flow Forecast - Predictive analysis chart
export const CashFlowForecast = ({ data, forecast, height = 300, formatCurrency }) => {
  const [viewMode, setViewMode] = useState('combined'); // 'combined', 'income', 'expenses'
  
  if (!data || data.length === 0) {
    return (
      <div style={{ 
        height: height, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: 'var(--gray-400)',
        fontSize: '0.875rem'
      }}>
        No forecast data available
      </div>
    );
  }

  const width = Math.max(600, data.length * 60 + (forecast?.length || 0) * 60);
  const padding = { left: 80, right: 40, top: 40, bottom: 80 };
  // const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;

  const allData = [...data, ...(forecast || [])];
  const values = allData.flatMap(d => [d.income || 0, Math.abs(d.expenses || 0)]);
  const maxValue = Math.max(...values);
  const minValue = Math.min(...values, 0);
  const range = maxValue - minValue || 1;

  // const getX = (index) => padding.left + (index * plotWidth) / Math.max(1, allData.length - 1);
  const getY = (value) => padding.top + plotHeight - ((value - minValue) / range) * plotHeight;

  return (
    <div style={{ width: '100%', height: height + 40 }}>
      {/* View mode controls */}
      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
        {['combined', 'income', 'expenses'].map(mode => (
          <button
            key={mode}
            onClick={() => setViewMode(mode)}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              border: viewMode === mode ? '2px solid var(--gray-800)' : '1px solid var(--gray-300)',
              borderRadius: '6px',
              background: viewMode === mode ? 'var(--gray-800)' : 'white',
              color: viewMode === mode ? 'white' : 'var(--gray-700)',
              cursor: 'pointer',
              textTransform: 'capitalize',
              transition: 'all 0.2s ease',
              outline: 'none'
            }}
            onMouseOver={(e) => {
              if (viewMode !== mode) {
                e.target.style.background = 'var(--gray-50)';
                e.target.style.borderColor = 'var(--gray-400)';
              }
            }}
            onMouseOut={(e) => {
              if (viewMode !== mode) {
                e.target.style.background = 'white';
                e.target.style.borderColor = 'var(--gray-300)';
              }
            }}
          >
            {mode}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', width: '100%', border: '1px solid var(--gray-200)', borderRadius: '8px', background: 'white' }}>
        {/* Fixed Y-axis */}
        <div style={{ flexShrink: 0, width: padding.left + 10 }}>
          <svg width={padding.left + 10} height={height} viewBox={`0 0 ${padding.left + 10} ${height}`}>
            <defs>
              <linearGradient id="forecastIncomeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="var(--income-green)" stopOpacity="0.3" />
                <stop offset="100%" stopColor="var(--income-green)" stopOpacity="0.05" />
              </linearGradient>
              <linearGradient id="forecastExpenseGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="var(--expense-red)" stopOpacity="0.3" />
                <stop offset="100%" stopColor="var(--expense-red)" stopOpacity="0.05" />
              </linearGradient>
            </defs>
            
            {/* Y-axis grid lines and labels */}
            {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => (
              <g key={`y-axis-${index}`}>
                <line
                  x1="0"
                  y1={padding.top + ratio * plotHeight}
                  x2={padding.left}
                  y2={padding.top + ratio * plotHeight}
                  stroke="var(--gray-200)"
                  strokeWidth="1"
                  strokeDasharray="2,4"
                />
                <text
                  x={padding.left - 10}
                  y={padding.top + ratio * plotHeight + 4}
                  textAnchor="end"
                  fontSize="11"
                  fill="var(--gray-500)"
                  fontWeight="500"
                >
                  {formatCurrency ? formatCurrency(maxValue - (ratio * (maxValue - minValue))) : `$${Math.round(maxValue - (ratio * (maxValue - minValue)))}`}
                </text>
              </g>
            ))}
          </svg>
        </div>

        {/* Scrollable chart area */}
        <div style={{ flex: 1, overflowX: 'auto', overflowY: 'hidden' }}>
          <svg width={width - padding.left} height={height} viewBox={`0 0 ${width - padding.left} ${height}`}>
            {/* Horizontal grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => (
              <line
                key={`h-grid-${index}`}
                x1="0"
                y1={padding.top + ratio * plotHeight}
                x2={width - padding.left - padding.right}
                y2={padding.top + ratio * plotHeight}
                stroke="var(--gray-200)"
                strokeWidth="1"
                strokeDasharray="2,4"
              />
            ))}

          {/* Historical data separator */}
          {forecast && forecast.length > 0 && (
            <line
              x1={(data.length - 1) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)}
              y1={padding.top}
              x2={(data.length - 1) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)}
              y2={padding.top + plotHeight}
              stroke="var(--gray-400)"
              strokeWidth="2"
              strokeDasharray="5,5"
            />
          )}

          {/* Income line and area */}
          {(viewMode === 'combined' || viewMode === 'income') && (
            <g>
              {/* Historical income area */}
              <path
                d={`M ${(0) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)} ${getY(data[0]?.income || 0)} ${data.map((item, index) => 
                  `L ${(index) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)} ${getY(item.income || 0)}`
                ).join(' ')} L ${(data.length - 1) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)} ${padding.top + plotHeight} L ${0} ${padding.top + plotHeight} Z`}
                fill="url(#forecastIncomeGradient)"
              />
              
              {/* Historical income line */}
              <path
                d={`M ${0} ${getY(data[0]?.income || 0)} ${data.map((item, index) => 
                  `L ${(index) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)} ${getY(item.income || 0)}`
                ).join(' ')}`}
                fill="none"
                stroke="var(--income-green)"
                strokeWidth="2.5"
                strokeLinecap="round"
              />

              {/* Forecast income area */}
              {forecast && forecast.length > 0 && (
                <>
                  <path
                    d={`M ${(data.length - 1) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)} ${getY(forecast[0]?.income || 0)} ${forecast.map((item, index) => 
                      `L ${(data.length - 1 + index + 1) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)} ${getY(item.income || 0)}`
                    ).join(' ')} L ${(allData.length - 1) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)} ${padding.top + plotHeight} L ${(data.length - 1) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)} ${padding.top + plotHeight} Z`}
                    fill="url(#forecastIncomeGradient)"
                    fillOpacity="0.5"
                  />
                  
                  <path
                    d={`M ${(data.length - 1) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)} ${getY(data[data.length - 1]?.income || 0)} ${forecast.map((item, index) => 
                      `L ${(data.length - 1 + index + 1) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)} ${getY(item.income || 0)}`
                    ).join(' ')}`}
                    fill="none"
                    stroke="var(--income-green)"
                    strokeWidth="2.5"
                    strokeDasharray="5,5"
                    strokeLinecap="round"
                  />
                </>
              )}
            </g>
          )}

          {/* Expense line and area */}
          {(viewMode === 'combined' || viewMode === 'expenses') && (
            <g>
              {/* Historical expenses area */}
              <path
                d={`M ${0} ${getY(Math.abs(data[0]?.expenses || 0))} ${data.map((item, index) => 
                  `L ${(index) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)} ${getY(Math.abs(item.expenses || 0))}`
                ).join(' ')} L ${(data.length - 1) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)} ${padding.top + plotHeight} L ${0} ${padding.top + plotHeight} Z`}
                fill="url(#forecastExpenseGradient)"
              />
              
              {/* Historical expenses line */}
              <path
                d={`M ${0} ${getY(Math.abs(data[0]?.expenses || 0))} ${data.map((item, index) => 
                  `L ${(index) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)} ${getY(Math.abs(item.expenses || 0))}`
                ).join(' ')}`}
                fill="none"
                stroke="var(--expense-red)"
                strokeWidth="2.5"
                strokeLinecap="round"
              />

              {/* Forecast expenses */}
              {forecast && forecast.length > 0 && (
                <>
                  <path
                    d={`M ${(data.length - 1) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)} ${getY(Math.abs(forecast[0]?.expenses || 0))} ${forecast.map((item, index) => 
                      `L ${(data.length - 1 + index + 1) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)} ${getY(Math.abs(item.expenses || 0))}`
                    ).join(' ')} L ${(allData.length - 1) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)} ${padding.top + plotHeight} L ${(data.length - 1) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)} ${padding.top + plotHeight} Z`}
                    fill="url(#forecastExpenseGradient)"
                    fillOpacity="0.5"
                  />
                  
                  <path
                    d={`M ${(data.length - 1) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)} ${getY(Math.abs(data[data.length - 1]?.expenses || 0))} ${forecast.map((item, index) => 
                      `L ${(data.length - 1 + index + 1) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)} ${getY(Math.abs(item.expenses || 0))}`
                    ).join(' ')}`}
                    fill="none"
                    stroke="var(--expense-red)"
                    strokeWidth="2.5"
                    strokeDasharray="5,5"
                    strokeLinecap="round"
                  />
                </>
              )}
            </g>
          )}

          {/* Data points for historical data */}
          {allData.slice(0, data.length).map((item, index) => (
            <g key={`point-${index}`}>
              {(viewMode === 'combined' || viewMode === 'income') && (
                <circle
                  cx={(index) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)}
                  cy={getY(item.income || 0)}
                  r="4"
                  fill="var(--income-green)"
                  stroke="white"
                  strokeWidth="2"
                />
              )}
              {(viewMode === 'combined' || viewMode === 'expenses') && (
                <circle
                  cx={(index) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)}
                  cy={getY(Math.abs(item.expenses || 0))}
                  r="4"
                  fill="var(--expense-red)"
                  stroke="white"
                  strokeWidth="2"
                />
              )}
            </g>
          ))}

          {/* Forecast data points */}
          {forecast && forecast.length > 0 && forecast.map((item, index) => (
            <g key={`forecast-point-${index}`}>
              {(viewMode === 'combined' || viewMode === 'income') && (
                <circle
                  cx={(data.length + index) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)}
                  cy={getY(item.income || 0)}
                  r="3"
                  fill="var(--income-green)"
                  fillOpacity="0.6"
                  stroke="white"
                  strokeWidth="1.5"
                />
              )}
              {(viewMode === 'combined' || viewMode === 'expenses') && (
                <circle
                  cx={(data.length + index) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)}
                  cy={getY(Math.abs(item.expenses || 0))}
                  r="3"
                  fill="var(--expense-red)"
                  fillOpacity="0.6"
                  stroke="white"
                  strokeWidth="1.5"
                />
              )}
            </g>
          ))}

          {/* X-axis labels */}
          {allData.map((item, index) => (
            <text
              key={`label-${index}`}
              x={(index) * (width - padding.left - padding.right) / Math.max(1, allData.length - 1)}
              y={padding.top + plotHeight + 20}
              textAnchor="middle"
              fontSize="10"
              fill={index < data.length ? "var(--gray-600)" : "var(--gray-400)"}
              fontWeight="500"
            >
              {item.period || ''}
            </text>
          ))}

          {/* Legend */}
          <g transform="translate(20, 15)">
            <circle cx="0" cy="0" r="4" fill="var(--income-green)" />
            <text x="10" y="4" fontSize="12" fill="var(--gray-600)" fontWeight="500">Income</text>
            <circle cx="80" cy="0" r="4" fill="var(--expense-red)" />
            <text x="90" y="4" fontSize="12" fill="var(--gray-600)" fontWeight="500">Expenses</text>
            {forecast && forecast.length > 0 && (
              <>
                <line x1="180" y1="0" x2="195" y2="0" stroke="var(--gray-400)" strokeWidth="2" strokeDasharray="3,3" />
                <text x="200" y="4" fontSize="12" fill="var(--gray-600)" fontWeight="500">Forecast</text>
              </>
            )}
          </g>
          </svg>
        </div>
      </div>
    </div>
  );
};

// Comparative Analysis - Multi-period comparison
export const ComparativeAnalysis = ({ data, height = 250, formatCurrency }) => {
  const [comparisonType, setComparisonType] = useState('monthOverMonth'); // 'monthOverMonth', 'yearOverYear', 'quarterlyTrend'

  if (!data || data.length === 0) {
    return (
      <div style={{ 
        height: height, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: 'var(--gray-400)',
        fontSize: '0.875rem'
      }}>
        No comparative data available
      </div>
    );
  }

  const width = 100; // Use percentage width
  const padding = { left: 15, right: 15, top: 50, bottom: 60 };

  return (
    <div style={{ width: '100%', height: height + 40 }}>
      {/* Comparison type selector */}
      <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
        <select
          value={comparisonType}
          onChange={(e) => setComparisonType(e.target.value)}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.875rem',
            border: '1px solid var(--gray-300)',
            borderRadius: '6px',
            background: 'white'
          }}
        >
          <option value="monthOverMonth">Month over Month</option>
          <option value="yearOverYear">Year over Year</option>
          <option value="quarterlyTrend">Quarterly Trend</option>
        </select>
      </div>

      <svg 
        width="100%" 
        height={height} 
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Comparison chart content will be rendered here based on comparisonType */}
        {data.map((period, index) => {
          const x = padding.left + (index * (width - padding.left - padding.right)) / Math.max(1, data.length - 1);
          const changePercent = period.change || 0;
          const isPositive = changePercent >= 0;
          const barHeight = Math.abs(changePercent) * 2; // Scale factor for visualization
          const centerY = height / 2;
          const barY = isPositive ? centerY - barHeight : centerY;
          
          return (
            <g key={`comparison-${index}`}>
              {/* Change bar */}
              <rect
                x={x - 8}
                y={barY}
                width={16}
                height={Math.abs(barHeight)}
                fill={isPositive ? 'var(--income-green)' : 'var(--expense-red)'}
                fillOpacity="0.8"
                rx="2"
              />
              
              {/* Change percentage */}
              <text
                x={x}
                y={isPositive ? barY - 5 : barY + barHeight + 15}
                textAnchor="middle"
                fontSize="10"
                fill={isPositive ? 'var(--income-green)' : 'var(--expense-red)'}
                fontWeight="600"
              >
                {isPositive ? '+' : ''}{changePercent.toFixed(1)}%
              </text>
              
              {/* Period label */}
              <text
                x={x}
                y={height - padding.bottom + 20}
                textAnchor="middle"
                fontSize="9"
                fill="var(--gray-500)"
              >
                {period.label}
              </text>
            </g>
          );
        })}
        
        {/* Center baseline */}
        <line
          x1={padding.left}
          y1={height / 2}
          x2={width - padding.right}
          y2={height / 2}
          stroke="var(--gray-300)"
          strokeWidth="1"
          strokeDasharray="2,2"
        />
      </svg>
    </div>
  );
};